// REFILLING A SHED RECEIPT (2026-10-12). The chat keeps its threads in the
// browser's storage under a byte budget (chat.js STORE_BUDGET), and a receipt
// that no longer fits is shed: its text and counts stay, its cards go
// (shedRun). On 2026-10-12 that emptied both receipts of a live demo - "50 more
// leads were selected — this receipt shows the first 0" over fifty drafted
// emails the server had filed minutes earlier.
//
// A receipt built on the server names its run (packRunMessage keeps
// `serverRun`), and the server keeps a run's rows for thirty days. So a shed
// receipt is read back from GET /enrich/operation-run and packed again, and the
// card renders the cards it lost. In memory only: writing them back into the
// store would only have them shed again. No React here; the hook that drives
// the card is in pages/Chat.jsx (useRefilledReceipt).
import { operationRun } from './enrich.js'
import { packRunMessage } from './chat.js'

// What a refill restores: every card array the packer produces. Counts, title,
// lines and the briefing's own facts (dismissed companies, trials ending later,
// an unread lane) are never shed and stay as stored.
export const REFILLED_FIELDS = [
  'leads',
  'tasks',
  'calls',
  'waiting',
  'manualReply',
  'inactiveUsers',
  'expiredSubscriptions',
  'trialUsers',
  'paymentFailed',
]

const short = (count, rows) => Number(count) > 0 && !(Array.isArray(rows) && rows.length > 0)

/**
 * Is this receipt showing fewer cards than it says it has? True for a copy
 * shedRun emptied (it says so), and for any built receipt whose counts name
 * cards its arrays do not hold. A receipt that built nothing has nothing to
 * refill and is never flagged.
 */
export function receiptLooksShed(msg) {
  if (!msg || msg.role !== 'run' || msg.outcome !== 'built') return false
  if (msg.runShed === true) return true
  return (
    short(msg.leadCount, msg.leads) ||
    short(msg.taskCount, msg.tasks) ||
    short(msg.inactiveUserCount, msg.inactiveUsers) ||
    short(msg.expiredSubscriptionCount, msg.expiredSubscriptions) ||
    short(msg.trialUserCount, msg.trialUsers)
  )
}

/**
 * The cards a shed receipt lost, read back from its server run. Resolves to
 * the refilled fields (REFILLED_FIELDS, as packRunMessage shapes them), or
 * null when the receipt names no run, the run is gone (thirty days), still
 * running, or not readable by this account - the card then stays as stored.
 *
 * @param {object} msg           the stored receipt message
 * @param {object} [opts.read]   the run reader, operationRun unless a test
 *                               hands in its own
 */
export async function refillReceipt(msg, { read = operationRun } = {}) {
  const runId = String(msg?.serverRun?.runId || '').trim()
  if (!runId) return null
  let answer
  try {
    answer = await read(runId)
  } catch {
    return null
  }
  if (!answer || answer.status !== 'SUCCEEDED' || !answer.receipt || typeof answer.receipt !== 'object') return null
  // Packed exactly as the run host packed it the first time, from the same
  // receipt - the server's pass reports are not the host's nodes, so none are
  // handed over; only the arrays are taken.
  const packed = packRunMessage({
    operationId: msg.operationId,
    operationLabel: msg.operationLabel || '',
    passes: [],
    result: { ...answer.receipt, serverRun: msg.serverRun },
    at: msg.at,
  })
  const fields = {}
  for (const field of REFILLED_FIELDS) {
    if (Array.isArray(packed[field])) fields[field] = packed[field]
  }
  return fields
}
